import {AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild} from '@angular/core';
import {StorageService} from '../../../services/storage/storage.service';
import {AuthService} from '../../../services/auth/auth.service';
import {cloneDeep} from 'lodash-es';
import moment from 'moment';
import {
  PaymentReversalPage,
  RefundStatusEnum
} from '../../../generated/sales-terminal-client';
import {ValidationService} from '../../../services/validation/validation.service';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {ViewService} from '../../../services/view/view.service';
import {finalize} from 'rxjs/operators';
import {Privileges} from '../../admin/privileges/privileges.constants';
import {UtilService} from '../../../services/util/util.service';
import {DataTable, DataTableRowItemType} from '../../../components/data-table/data-table.interfaces';
import {MatTabGroup} from '@angular/material/tabs';
import {PaymentReversalsFilter, RefundService} from '../../../services/refund/refund.service';
import {MatCheckboxChange} from "@angular/material/checkbox";
import {PaymentReversalRefundConfirmationComponent} from "./refund-confirmation/payment-reversal-refund-confirmation.component";
import {MatDialog} from "@angular/material/dialog";
import {SnackService} from "../../../services/snack/snack.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-payment-reversal',
  templateUrl: './payment-reversal.component.html',
  styleUrls: ['./payment-reversal.component.scss']
})
export class PaymentReversalComponent implements AfterViewInit, OnDestroy {
  static STORAGE_SEARCH_RESULTS_PAGE_SIZE = 'paymentReversalResults.pageSize';
  static STORAGE_SEARCH_RESULTS_PAGE = 'paymentReversalResults.page';
  static STORAGE_SEARCH_RESULTS_FILTERS = 'paymentReversalResults.filters';

  @ViewChild('tableRefundDate')
  tableRefundDate: TemplateRef<any>;
  @ViewChild('tableEvent')
  tableEvent: TemplateRef<any>;
  @ViewChild('tableClient')
  tableClient: TemplateRef<any>;
  @ViewChild('tableClientAccount')
  tableClientAccount: TemplateRef<any>;
  @ViewChild('tableSender')
  tableSender: TemplateRef<any>;
  @ViewChild('tableTotal')
  tableTotal: TemplateRef<any>;
  @ViewChild('tableResponse')
  tableResponse: TemplateRef<any>;

  @ViewChild('tabGroup') tabGroupElement: MatTabGroup;

  public tab: number = 0;
  public filters: PaymentReversalsFilter = {};

  public results: PaymentReversalPage = {items: [], total: 0};
  public pageSizeOptions: number[] = [30, 60, 90];
  public pageSize: number = this.pageSizeOptions[0];
  public page = 1;
  public isLoading = false;
  public noSearches = true;
  public showMobileFilters: boolean = false;
  public expandedTransactions: number[] = [];
  private _subscriptions: Subscription[] = [];
  public statusTabs: RefundStatusEnum[] = Object.values(RefundStatusEnum);
  public statusTab: number = this.statusTabs.indexOf(RefundStatusEnum.NotStarted);
  public selectedIds: number[] = [];
  public eventId: number;
  private _clickable: number[] = [];
  public dataTableConfig: DataTable;

  constructor(public authService: AuthService,
              public validationService: ValidationService,
              public viewService: ViewService,
              public utilService: UtilService,
              private refundService: RefundService,
              private storageService: StorageService,
              private dialog: MatDialog,
              private snackService: SnackService,
              private translateService: TranslateService,
              private router: Router) {
    if (storageService.get(PaymentReversalComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE)) {
      this.pageSize = parseInt(storageService.get(PaymentReversalComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE), 0);
    }
    if (storageService.get(PaymentReversalComponent.STORAGE_SEARCH_RESULTS_PAGE)) {
      this.page = parseInt(storageService.get(PaymentReversalComponent.STORAGE_SEARCH_RESULTS_PAGE), 0);
    }
    this._subscriptions.push(router.events.subscribe((event) => {
      if (this.isComponentReload(event)) {
        if (this.results.total !== 0) {
          setTimeout(() => {
            if (storageService.get(PaymentReversalComponent.STORAGE_SEARCH_RESULTS_FILTERS)) {
              this.filters = JSON.parse(storageService.get(PaymentReversalComponent.STORAGE_SEARCH_RESULTS_FILTERS));
            }
            this.search(this.page);
          }, 0);
        }
        this.setup();
      }
    }));
  }

  private isComponentReload(event: any): boolean {
    return event instanceof NavigationEnd &&
      event.url === '/app/sales-terminal/payment-reversal'
  }

  public ngAfterViewInit(): void {
    this.search();
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(value => value.unsubscribe());
  }

  public setup(): void {
    this.dataTableConfig = {
      header: {
        items: [
          {
            key: 'selected',
            value: false,
            format: '',
            type: DataTableRowItemType.Checkbox,
            cssClass: 'min-width',
            isHidden: this.clickable?.length === 0
          },
          {
            key: 'order_confirmation',
            format: 'general.api_payment_reversal_order_confirmation'
          },
          {
            key: 'refund_date',
            format: 'general.api_payment_reversal_refund_date'
          },
          {
            key: 'refund_transaction_id',
            format: 'general.api_payment_reversal_refund_transaction_id'
          },
          {
            key: 'client',
            format: 'general.api_payment_reversal_client'
          },
          {
            key: 'event',
            format: 'general.api_payment_reversal_event',
            children: [{
              key: 'event_date_start',
              format: 'general.api_payment_reversal_event_date',
              cssClass: 'subtitle'
            }],
            width: 20
          },
          {
            key: 'client_account',
            format: 'general.api_payment_reversal_client_account'
          },
          {
            key: 'sender',
            format: 'general.api_payment_reversal_sender',
            isHidden: !this.pendingOrCompletedOrFailed
          },
          {
            key: 'response',
            format: 'general.api_payment_reversal_response',
            isHidden: !this.completedOrFailed
          },
          {
            key: 'total',
            format: 'general.api_payment_reversal_table_total',
            cssClass: 'align-right',
          }
        ]
      },
      body: {
        items: [
          {
            key: 'selected',
            value: 'id',
            type: DataTableRowItemType.Checkbox,
            isHidden: this.clickable?.length === 0
          },
          {
            key: 'order_confirmation',
            format: 'orderConfirmation',
            useModel: true
          },
          {
            key: 'refund_date',
            type: DataTableRowItemType.Template,
            template: this.tableRefundDate
          },
          {
            key: 'refund_transaction_id',
            format: 'refundTransactionId',
            useModel: true
          },
          {
            key: 'client',
            type: DataTableRowItemType.Template,
            template: this.tableClient
          },
          {
            key: 'event',
            type: DataTableRowItemType.Template,
            template: this.tableEvent
          },
          {
            key: 'client_account',
            type: DataTableRowItemType.Template,
            template: this.tableClientAccount
          },
          {
            key: 'sender',
            type: DataTableRowItemType.Template,
            template: this.tableSender,
            isHidden: !this.pendingOrCompletedOrFailed
          },
          {
            key: 'response',
            type: DataTableRowItemType.Template,
            template: this.tableResponse,
            isHidden: !this.completedOrFailed
          },
          {
            key: 'total',
            type: DataTableRowItemType.Template,
            template: this.tableTotal,
            cssClass: 'align-right'
          }
        ]
      }
    };
  }

  public get completedOrFailed(): boolean {
    return [RefundStatusEnum.Completed, RefundStatusEnum.Failed].includes(this.statusTabs[this.statusTab]);
  }

  public get notStartedOrFailed(): boolean {
    return [RefundStatusEnum.NotStarted, RefundStatusEnum.Failed].includes(this.statusTabs[this.statusTab]);
  }

  public get pendingOrCompletedOrFailed(): boolean {
    return [RefundStatusEnum.Pending, RefundStatusEnum.Completed, RefundStatusEnum.Failed].includes(this.statusTabs[this.statusTab]);
  }

  public get clickable(): number[] {
    return this._clickable || [];
  }
  public set clickable(trxIds: number[]) {
    this._clickable = trxIds;
    this.selectedIds = cloneDeep(this._clickable);
  }

  public isSelected(trxId: number): boolean {
    return this.selectedIds.includes(trxId);
  }

  public areAllSelected(): boolean {
    return this.selectedIds.length === this.clickable.length;
  }

  public areSomeSelected(): boolean {
    return !!(this.selectedIds.length && !this.areAllSelected());
  }

  public toggle(checkboxChange: MatCheckboxChange, trxId: number): void {
    if (checkboxChange.checked) {
      this.selectedIds.push(trxId);
    } else {
      this.selectedIds.splice(this.selectedIds.indexOf(trxId), 1);
    }
  }

  public toggleAll(checkboxChange: MatCheckboxChange): void {
    if (checkboxChange.checked) {
      this.selectedIds = cloneDeep(this.clickable);
    } else {
      this.selectedIds = [];
    }
  }

  public search(page = 1): void {
    this.expandedTransactions = [];
    this.showMobileFilters = false;
    this.isLoading = true;
    this.noSearches = false;
    this.results = {items: [], total: 0};
    this.page = page;
    const filters = cloneDeep(this.filters);
    filters.page = this.page;
    filters.pageSize = this.pageSize;
    if (filters.reversalStartDate) {
      filters.reversalStartDate = moment(filters.reversalStartDate).format('YYYY-MM-DD');
    }
    const diff = moment(filters.reversalEndDate).diff(filters.reversalStartDate, 'days');
    if (diff < 0) {
      filters.reversalEndDate = filters.reversalStartDate;
    }
    if (filters.reversalEndDate) {
      filters.reversalEndDate = moment(filters.reversalEndDate).format('YYYY-MM-DD');
    }
    filters.refundStatus = this.statusTabs[this.statusTab];

    const searchSub = this.refundService.getPaymentReversalsList(filters).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((result: PaymentReversalPage) => {
      this.results = result;
      this.eventId = filters.eventId;
      this.clickable = this.notStartedOrFailed ? result.items.map(trx => trx.id) : [];
      this.setup();
      this.storageService.set(PaymentReversalComponent.STORAGE_SEARCH_RESULTS_FILTERS, JSON.stringify(this.filters));
    });
    this._subscriptions.push(searchSub);
  }

  public sendToRefund(): void {
    let amount = 0, sum = 0;
    this.results.items.forEach(trx => {
      if (this.selectedIds.includes(trx.id)) {
        amount++;
        sum += trx.totalAmount;
      }
    });
    this.openRefundDialog(amount, sum, () => {
      this.isLoading = true;
      this.refundService.sendPaymentReversalsToEveryPay(this.selectedIds).pipe(
        finalize(() => this.search())
      ).subscribe(() => {
        this.snackService.showSuccess(this.translateService.instant('general.api_payment_reversal_sent'));
      });
    });
  }

  public sendEventToRefund(): void {
    this.refundService.getEventPaymentReversalsDetails(this.filters.eventId).subscribe(res => {
      this.openRefundDialog(res.count, res.totalAmount, () => {
        this.isLoading = true;
        this.refundService.sendEventPaymentReversalsToEveryPay(res.eventId).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe(() => {
          this.search();
          this.snackService.showSuccess(this.translateService.instant('general.api_payment_reversal_event_sent'));
        });
      });
    });
  }

  private openRefundDialog(totalCount: number, totalAmount: number, callback: Function): void {
    const dialogRef = this.dialog.open(PaymentReversalRefundConfirmationComponent, {
      width: '50%',
      panelClass: 'xs-popup',
      data: {
        totalCount,
        totalAmount
      }
    });
    dialogRef.afterClosed().subscribe((data: boolean) => {
      if (data) {
        callback();
      }
    });
  }

  public updateLocalStorage(): void {
    this.search();
    this.storageService.set(PaymentReversalComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE, this.pageSize);
  }

  protected readonly Privileges = Privileges;
}
